import type { HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

/**
 * Skeleton — the loading shape of the content that is coming (05 §6.3).
 * Never a full-page spinner after the first load: a list renders skeleton rows
 * that match the real table, so nothing jumps when the data lands.
 *
 * `aria-hidden`: the surrounding region carries `aria-busy`, and a screen reader
 * has nothing to gain from the grey boxes.
 */
export type SkeletonProps = HTMLAttributes<HTMLDivElement>;

export function Skeleton({ className, ...props }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse rounded-md bg-muted', className)}
      {...props}
    />
  );
}

export interface SkeletonTextProps {
  /** How many lines to draw. */
  lines?: number;
  className?: string;
}

/** A paragraph-shaped placeholder; the last line is short, like real text. */
export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} className={cn('h-4', index === lines - 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </div>
  );
}
